/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import styled from 'styled-components';
import { isEmpty } from 'lodash';
import { List, Loader } from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { Table, Avatar } from '../../../components';
import { FormattedMessage } from 'react-intl';

import AdministratorDetailsButton from './AdministratorDetailsButton';
import EditAdministratorButton from './EditAdministratorButton';
import DeleteAdministratorButton from './DeleteAdministratorButton';

import type { Administrator } from '../../../type';

export const GET_ADMINISTRATORS_QUERY = gql`
    query getAdministrators($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    administrators {
                        id
                        firstName
                        lastName
                        phone
                        email
                        roles
                    }
                }
            }
        }
    }
`;

type AdministratorRowProps = {
    administrator: Administrator,
};

const NameCell = styled(Table.Cell)`
    padding: 0px !important;
`;

const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={2} textAlign="center">
            <FormattedMessage
                id="pages.administrators.administratorsList.noAdministrators"
                defaultMessage="You don't have administrators yet"
            />
        </Table.Cell>
    </Table.Row>
);

const AdministratorRow = ({ administrator }: AdministratorRowProps) => (
    <Table.Row>
        <NameCell>
            <AdministratorDetailsButton administrator={administrator}>
                <Avatar
                    name={`${administrator.firstName} ${
                        administrator.lastName
                    }`}
                    size={24}
                    spaced="right"
                    round
                />
                <span>
                    {administrator.firstName} {administrator.lastName}
                </span>
            </AdministratorDetailsButton>
        </NameCell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditAdministratorButton administrator={administrator} />
                </List.Item>
                <List.Item>
                    <DeleteAdministratorButton administrator={administrator} />
                </List.Item>
            </List>
        </Table.Cell>
    </Table.Row>
);

const AdministratorsList = () => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ staff }) => staff.get('searchQuery'));

    const { data, loading } = useQuery(GET_ADMINISTRATORS_QUERY, {
        variables: {
            salon,
            search: searchQuery || undefined,
        },
        fetchPolicy: 'cache-and-network',
    });

    const administrators = data?.viewer?.salon?.administrators ?? [];

    if (loading) {
        return <Loader size="big" inline="centered" active />;
    }

    return (
        <Table celled selectable striped fixed>
            <Table.Header>
                <Table.Row>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.administrators.administratorsList.name"
                            defaultMessage="Name"
                        />
                    </Table.HeaderCell>
                    <Table.HeaderCell>
                        <FormattedMessage
                            id="pages.administrators.administratorsList.actions"
                            defaultMessage="Actions"
                        />
                    </Table.HeaderCell>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {isEmpty(administrators) ? (
                    <EmptyMessage />
                ) : (
                    administrators.map(administrator => (
                        <AdministratorRow
                            key={administrator.id}
                            administrator={administrator}
                        />
                    ))
                )}
            </Table.Body>
        </Table>
    );
};

export default AdministratorsList;
